/**
 * Persistence for the "Don't show this again" opt-out on the Buyback
 * confirmation modal. Two-tier read/write because partner iframes
 * sometimes run in environments that block one storage class but not
 * another:
 *
 *   1. `localStorage`   — survives reloads + new sessions. The ideal.
 *   2. `sessionStorage` — survives same-tab reload but not a new tab.
 *      Some 3rd-party-iframe environments block `localStorage` while
 *      still allowing `sessionStorage`.
 *
 * The previous version had a third tier — a module-level in-memory
 * flag — that survived HMR and Local Storage purges, leaving the
 * modal latched away with no clean way for the user to reset it
 * without restarting the dev server. Removed: storage-only now, so
 * clearing Local Storage actually clears the opt-out.
 *
 * Reads check tiers in order; the first hit wins. Writes go to both
 * (best-effort) so the next read picks up whichever tier the
 * partner environment allows.
 */

// Bumped from `…buybackSkipConfirm` after we switched the persisted
// value from a boolean `'true'` to a `'accept' | 'acceptAndRip'`
// action. Versioning the key sidesteps two real-world headaches:
//   • partner iframes are often cross-origin, so clearing storage in
//     the parent DC tab does nothing for the embedded iframe.
//   • stale persisted booleans from earlier versions would otherwise
//     be misread as the new string-action format.
// A fresh key starts everyone with `null` — modal shows again on the
// next Buyback click until the user re-ticks "Don't show this again".
const SKIP_STORAGE_KEY = 'repackz.buybackSkipConfirm';

/**
 * Remembered Buyback action. Stored when the user ticks "Don't show
 * this again" — on the *next* Buyback click we replay the same action
 * verbatim instead of re-opening the modal. Was previously a boolean
 * (`'true'` for "skip") which lost track of which path the user had
 * picked and always defaulted to `accept`.
 */
export type SkipBuybackAction = 'accept' | 'acceptAndRip';

function isAction(v: unknown): v is SkipBuybackAction {
  return v === 'accept' || v === 'acceptAndRip';
}

function fromRaw(v: string | null): SkipBuybackAction | null {
  if (isAction(v)) return v;
  // Legacy migration: earlier versions stored the literal `'true'` to
  // mean "skip the modal" without recording which CTA the user had
  // chosen. Treat those as `accept` (the original default behaviour)
  // so existing partners don't lose their opt-out.
  if (v === 'true') return 'accept';
  return null;
}

export function readSkipBuybackConfirm(): SkipBuybackAction | null {
  try {
    const hit = fromRaw(window.localStorage.getItem(SKIP_STORAGE_KEY));
    if (hit) return hit;
  } catch {
    // ignore — blocked storage, fall through to next tier
  }
  try {
    const hit = fromRaw(window.sessionStorage.getItem(SKIP_STORAGE_KEY));
    if (hit) return hit;
  } catch {
    // ignore — fall through
  }
  return null;
}

export function writeSkipBuybackConfirm(action: SkipBuybackAction): void {
  try {
    window.localStorage.setItem(SKIP_STORAGE_KEY, action);
  } catch {
    // ignore
  }
  try {
    window.sessionStorage.setItem(SKIP_STORAGE_KEY, action);
  } catch {
    // ignore
  }
}
